import { action, computed, observable } from 'mobx';
import countBy from 'lodash/countBy';
import lodashGet from 'lodash/get';
import { RouterStore } from 'mobx-router5';
import { VocabularyItem, VocabularyNamespaces } from 'types';
import VocabularyStore from 'stores/Vocabulary';

export type FilterState = {
  key: string;
  value: string;
  count: number;
  checked: boolean;
};

/**
 * Configuration for a catalog filter.
 */
export type FilterConfiguration<EntryType> = {
  /**
   * The key of the filter, used to access the filter in the filters object.
   */
  key: string;
  /**
   * Filter title to display in the UI
   */
  title?: string;
  /**
   * A function that extracts filter values from each catalog entry.
   */
  extractFilterValues: (
    entries: EntryType[],
    vocabulary: VocabularyItem[],
  ) => (string | undefined)[] | VocabularyItem[];
  /**
   * A list of language labels to use for the filter values. If 'extractFilterValues' returns VocabularyItems, this is ignored.
   */
  vocabulary?: VocabularyItem[];
  /**
   * The namespace of the vocabulary to use for the filter values. If 'vocabulary' is provided, this is ignored.
   */
  vocabularyNamespace?: VocabularyNamespaces;
  /**
   * A compare function used to sort filter options. If not provided, will sort by count in descending order.
   */
  compareFn?: (a: FilterState, b: FilterState) => number;
  /**
   * A function that validates if a catalog entry matches the filter.
   * Used to filter the catalog entries based on the selected filter values.
   * @param entry Catalog entry to check
   * @param checkedOptions The selected filter values. Eg: ['platform_engineer', 'network_architect']
   * @returns Whether the entry matches the filter or not
   */
  validateEntry: (entry: EntryType, checkedOptions: string[]) => boolean;
  /**
   * If true, filter options with identical labels will be merged into a single UI option.
   * Clicking that option will toggle every filter option key
   */
  groupIdenticalLabels?: boolean;
};

/**
 * This class only handles filter groups with checkboxes.
 * If you need a different type of filter, you will need to extend this class:
 * Add extra functionalities by implementing the abstract methods, mainly filterEntries and getFilterInfo.
 */
abstract class FiltersStore<EntryType> {
  @observable filters: Record<string, FilterState[]>; // TODO: find a way to make this name dynamic

  @observable filterInfoLoaded: boolean = false;

  @observable filterInfoLoading: boolean = false;

  /**
   * Keyed by filter config key. Set to true when that group's vocabulary fetch rejected.
   * The group still "settles" (filterInfoLoaded becomes true) so a single failing group
   * doesn't block the rest of the filter panel from ever loading.
   */
  @observable filterInfoErrors: Record<string, boolean> = {};

  @observable expandedFilterGroups: Set<string> = new Set();

  routerStore?: RouterStore;

  constructor(
    filters: Record<string, FilterState[]>,
    routerStore?: RouterStore,
  ) {
    this.filters = filters;
    this.routerStore = routerStore;
  }

  /**
   * abstract method to be implemented by the extending class.
   * Responsible for filtering entries based on the current filter state.
   */
  filterEntries: () => void;

  /**
   * abstract method to be implemented by the extending class.
   * Responsible for populating the filters object with filter options.
   */
  getFilterInfo: (...args: unknown[]) => unknown;

  /**
   * abstract method to be implemented by the extending class.
   * Responsible for returning an array of filter configurations.
   */
  getFilterConfigs: () => FilterConfiguration<EntryType>[];

  /**
   * Loads the filter information for the catalog entries.
   * This is used to populate the filters in the UI.
   * This loads the vocabulary data for each filter type and then renders the filters based on
   * what is available in the the catalog entries. This ensures that filters only show up if they
   * exist in at least on entry.
   */
  __getFilterInfo = async (
    language: string,
    vocabularyStore: VocabularyStore,
    entryList: Array<EntryType> = [],
    fieldName: string = 'filters',
  ) => {
    this.filterInfoLoading = true;
    const filterConfigurations = this.getFilterConfigs();

    try {
      const { getVocabularyByNamespace } = vocabularyStore;

      const settledVocabularies = await Promise.allSettled(
        filterConfigurations.map((config) => {
          if (config.vocabulary) return config.vocabulary;
          if (config.vocabularyNamespace)
            return getVocabularyByNamespace(
              config.vocabularyNamespace,
              language,
            );
          return [];
        }),
      );

      const newErrors: Record<string, boolean> = {};
      const vocabularies: VocabularyItem[][] = settledVocabularies.map(
        (result, index) => {
          if (result.status === 'rejected') {
            newErrors[filterConfigurations[index].key] = true;
            return [];
          }
          return result.value ?? [];
        },
      );

      const isVocabularyItem = (
        item: string | VocabularyItem | undefined | null,
      ): item is VocabularyItem =>
        item !== undefined &&
        item !== null &&
        typeof item === 'object' &&
        'token' in item;

      const filterResults = filterConfigurations.map((config, index) => {
        const vocabulary = vocabularies[index] ?? [];
        const denormalizedValues =
          config.extractFilterValues(entryList, vocabulary) ?? [];

        const values = denormalizedValues
          .filter(
            (item): item is string | VocabularyItem =>
              item !== undefined && item !== null,
          )
          .map((item) => (isVocabularyItem(item) ? item.token : item));

        const newVocabulary = [
          ...vocabulary,
          ...denormalizedValues.filter(isVocabularyItem),
        ];

        return {
          key: config.key,
          filters: FiltersStore.renderFilters({
            values,
            vocabulary: newVocabulary,
            compareFn: config.compareFn,
          }),
        };
      });

      this[fieldName] = Object.fromEntries(
        filterResults.map((r) => [r.key, r.filters]),
      );

      this.filterInfoErrors = newErrors;
      this.filterInfoLoaded = true; // settles regardless of partial errors
      this.filterInfoLoading = false;

      return this[fieldName];
    } catch (err) {
      // only truly unexpected/synchronous errors land here now (e.g. getFilterConfigs() throwing)
      console.error('Error in getFilterInfo:', err);
      this.filterInfoLoading = false;
      this.filterInfoLoaded = true; // still "settled" — everything failed
      this.filterInfoErrors = Object.fromEntries(
        filterConfigurations.map((c) => [c.key, true]),
      );
      return null;
    }
  };

  /**
   * Replace the Set on every change so MobX observers and React context
   * (memoized on the Set reference) re-render. In-place add/delete keeps the
   * same reference, which leaves controlled NavExpandable isExpanded stuck.
   */
  @action toggleFilterGroups = (id: string, expanded?: boolean) => {
    const next = new Set(this.expandedFilterGroups);
    if (expanded) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.expandedFilterGroups = next;
  };

  /**
   * Checks or unchecks a filter
   * @param filterGroup Filter group id (eg. 'deliveryFormat')
   * @param filterKey Id of the clicked filter (eg. 'exam')
   * @param value (optional) boolean value to save as checked state
   * @param computeEntries if true, entries are filtered based on new filter state (default=true)
   */
  @action toggleFilterOption = (
    filterGroup: string,
    filterKeys: string | string[],
    value?: boolean,
    computeEntries: boolean = true,
  ) => {
    const keyList = Array.isArray(filterKeys) ? filterKeys : [filterKeys];

    keyList.forEach((filterKey) => {
      const filter: FilterState = this.filters[filterGroup]?.find(
        (f: FilterState) => f.key === filterKey,
      );
      if (filter) {
        if (typeof value === 'boolean') {
          filter.checked = value;
        } else {
          filter.checked = !filter.checked;
        }
        this.updateURLFilters(filterGroup, filterKey);
      }
    });

    if (computeEntries) {
      this.filterEntries();
    }
  };

  @action
  clearFilters(exceptions: string[] = [], computeEntries: boolean = true) {
    this.filterKeys.forEach((key) => {
      this.filters[key].forEach((item: FilterState) => {
        const skipped = exceptions.includes(item.key);

        if (!skipped) {
          // eslint-disable-next-line no-param-reassign
          item.checked = false;
        }
      });
    });

    if (computeEntries) {
      this.filterEntries();
    }
  }

  /**
   * Presets the filters based on an array of filter keys.
   * @param {Object} baseFilters - an object of filter keys and values to preset
   * @param {boolean} computeEntries - whether to compute the entries after presetting the filters
   */
  @action presetFilters = (
    baseFilters: { [key: string]: string } | null = null,
    computeEntries: boolean = true,
  ) => {
    const route = this.routerStore?.route as any;
    const routeParams = route?.params || {};
    // When baseFilters is passed (e.g. from Catalog view), let it take precedence over
    // routeParams so that explicitly passed params are used and avoid stale router state.
    const filters =
      baseFilters && Object.keys(baseFilters).length > 0
        ? { ...routeParams, ...baseFilters }
        : routeParams;

    this.filterKeys.forEach((key) => {
      const filterValue = filters[key];
      if (!filterValue) return;

      this.filters[key].forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        if (filterValue.includes(item.key)) item.checked = true;
      });
    });

    const nextExpanded = new Set<string>();
    this.filterKeys.forEach((key) => {
      if (this.filters[key]?.some((item) => item.checked)) {
        nextExpanded.add(key);
      }
    });
    this.expandedFilterGroups = nextExpanded;

    if (computeEntries) {
      this.filterEntries();
    }
  };

  @action updateURLFilters = (filterGroup: string, filterKeys: string) => {
    if (this.routerStore?.route) {
      const routeParams = (this.routerStore?.route as any)?.params || {};

      // Get current filter items from URL
      const currentParam = routeParams[filterGroup] as string;
      const filterGroupItems =
        currentParam && currentParam.trim()
          ? currentParam
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : [];

      const keyIndex = filterGroupItems.indexOf(filterKeys);
      const newfilterGroupItems =
        keyIndex >= 0
          ? filterGroupItems.filter((item) => item !== filterKeys)
          : [...filterGroupItems, filterKeys];

      this.routerStore.navigate(
        this.routerStore.route.name,
        {
          ...routeParams,
          [filterGroup]: newfilterGroupItems.length
            ? newfilterGroupItems.join(',')
            : undefined,
        },
        // @ts-ignore // this is a bug in the types
        { replace: true },
      );
    }
  };

  @computed get filterKeys(): string[] {
    return Object.keys(this.filters);
  }

  /**
   * Recalculates filter option counts using "faceted-search" rules
   *
   * For each filter group (our facets), counts are derived from entries filtered by every
   * other group's checked options (and optionally a search query received by params), but never
   * by that group's own selection
   *
   * @param {EntryType[]} allEntries - Full entry list before sidebar filters.
   * @param {(entries: EntryType[]) => EntryType[]} [applySearch] - Optional
   *   callback to apply the current search query to the entry set used for
   *   counting (like a Fuse-based text search or a simple search query).
   */
  @action
  recalculateFilterCountsFromEntries = (
    allEntries: EntryType[],
    applySearch: (entries: EntryType[]) => EntryType[] = (entries) => entries,
  ) => {
    const filterConfigurations = this.getFilterConfigs();
    const filterConfigurationsMap = Object.fromEntries(
      filterConfigurations.map((item) => [item.key, item]),
    );

    this.filterKeys.forEach((key) => {
      const filtersExcludingGroup = Object.fromEntries(
        this.filterKeys.map((filterKey) => [
          filterKey,
          filterKey === key
            ? this.filters[filterKey].map((item) => ({
                ...item,
                checked: false,
              }))
            : this.filters[filterKey],
        ]),
      );

      const entries = applySearch(
        FiltersStore.getFilteredEntries(
          allEntries,
          filterConfigurations,
          filtersExcludingGroup,
        ),
      );

      const newFilterCounts = countBy(
        filterConfigurationsMap[key]?.extractFilterValues(entries, []) ?? [],
      );

      this.filters[key].forEach((item: FilterState) => {
        // eslint-disable-next-line no-param-reassign
        item.count = newFilterCounts[item.key] || 0;
      });
    });
  };

  /**
   * Flattens the filters for a given key and returns an array of the checked keys.
   */
  static flattenCheckedFilters = (
    key: string,
    filters: Record<string, FilterState[]>,
  ): string[] => {
    if (!Array.isArray(filters[key])) {
      return [];
    }
    return filters[key]
      .filter((item: FilterState) => item.checked)
      .map((item: FilterState) => item.key);
  };

  /**
   * Given a list of entries, filter configurations, and filter states, returns a filtered list of entries.
   * @param list list containing the entries
   * @param filterConfigurations Filter configurations
   * @param filterState Filter state containing what options are checked
   * @param entryAcessor Function to extract the catalog entry from the object
   * @returns a filtered list of entries
   */
  static getFilteredEntries = <T>(
    /**
     * either a list of catalog entries or a list of objects that contain catalog entries
     */
    list: Array<T>,
    /**
     * an array of filter configurations
     */
    filterConfigurations: Pick<
      FilterConfiguration<T>,
      'key' | 'validateEntry'
    >[],
    /**
     * a record of filter states. The key is the filter key and the value is either an array of FilterState objects or a record of key-value pairs
     */
    filterState:
      | Record<string, FilterState[]>
      | Record<string, Record<string, boolean>>,
    /**
     * (optional) a function that extracts the catalog entry from the object
     */
    entryAcessor: (obj: unknown) => T = (entry) => entry as T,
  ): T[] => {
    if (!list) return [];

    const activeFiltersMap = new Map<string, string[]>();

    filterConfigurations.forEach((config) => {
      const state = filterState[config.key];
      let currentFilterSelection: string[] = [];

      if (state) {
        if (Array.isArray(state)) {
          currentFilterSelection = FiltersStore.flattenCheckedFilters(
            config.key,
            {
              [config.key]: state,
            },
          );
        } else {
          currentFilterSelection = Object.keys(state).filter(
            (key) => state[key],
          );
        }
      }

      if (currentFilterSelection.length > 0) {
        activeFiltersMap.set(config.key, currentFilterSelection);
      }
    });

    if (activeFiltersMap.size === 0) {
      return list;
    }

    return list.filter((e) => {
      const entry = entryAcessor(e);

      if (!entry) return false;

      return filterConfigurations.every((config) => {
        const currentFilterSelection = activeFiltersMap.get(config.key);

        if (!currentFilterSelection) {
          return true;
        }

        const matchCriteria = config.validateEntry(
          entry,
          currentFilterSelection,
        );

        return matchCriteria;
      });
    });
  };

  /**
   * Calculates the count for each filter option and sorts them according to a compare function.
   *
   * Given an array of values, returns an array of filter objects with:
   * - `key`: the filter token
   * - `value`: the display name (from vocabulary or fallback)
   * - `count`: number of occurrences in `values`
   * - `checked`: whether it is pre-selected
   *
   * @param {Object} params
   * @param {string[]} params.values - An array of filter values to be counted for each filter option.
   * @param {VocabularyItem[]} [params.vocabulary=[]] - An array of vocabulary items, each with `display_name` and `token`.
   * @param {(a: FilterState, b: FilterState) => number} [params.compareFn=null] - Optional custom comparator to sort the results.
   * @param {string[]} [params.preSelected=[]] - An array of pre-selected filter keys.
   * @param {'ignore' | 'useValues'} [params.missingVocabularyPolicy='ignore'] -
   *   If `'ignore'`, filters without a vocabulary entry are skipped;
   *   if `'useValues'`, they are rendered with their raw value as display name.
   *
   * @returns {FilterState[]} The sorted list of filters with counts and selection state.
   */
  static renderFilters({
    values,
    vocabulary = [],
    compareFn = null,
    preSelected = [],
    missingVocabularyPolicy = 'ignore',
  }: {
    values: string[];
    vocabulary?: VocabularyItem[];
    compareFn?: ((a: FilterState, b: FilterState) => number) | null;
    preSelected?: string[];
    missingVocabularyPolicy?: 'ignore' | 'useValues';
  }): FilterState[] {
    if (!Array.isArray(values) || values.length === 0) {
      return [];
    }

    // Count occurrences
    const count = values.reduce<Record<string, number>>((acc, val) => {
      acc[val] = (acc[val] ?? 0) + 1;
      return acc;
    }, {});

    const vocabMap = vocabulary.reduce<Record<string, string>>((acc, item) => {
      acc[item.token] = item.display_name;
      return acc;
    }, {});
    const preSelectedSet = new Set(preSelected);

    const filters = Object.entries(count).flatMap(([key, countValue]) => {
      const display = vocabMap[key];
      if (!display && missingVocabularyPolicy === 'ignore') return [];
      return [
        {
          key,
          value: display || key,
          count: countValue,
          checked: preSelectedSet.has(key),
        },
      ];
    });

    const compareByCount = (a: FilterState, b: FilterState) =>
      b.count - a.count;
    return filters.sort(compareFn || compareByCount);
  }
}

/**
 *  Prioritizes a field in an entry based on an array of fields.
 * @param {unknown} entry  - the entry to prioritize the field for
 * @param {Array} fields  - an array of fields to prioritize
 * @param {Boolean} isArray - if true, the field is expected to be an array
 * @returns  the prioritized field value
 */
export const prioritizeEntryField = <EntryType>(
  entry: EntryType,
  fields: string[],
  isArray: boolean,
) => {
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    const value = lodashGet(entry, field);
    if (isArray) {
      if (Array.isArray(value) && value.length > 0) {
        return value;
      }
    } else if (value) {
      return value;
    }
  }
  return isArray ? [] : null;
};

export default FiltersStore;
